/**
 * Centralized, typed access to environment variables.
 *
 * Why this file exists:
 *   Vite injects env vars on `import.meta.env`. Touching that object directly
 *   throughout the codebase makes refactors and renames painful and makes it
 *   easy to ship a typo. Every env read goes through here so the surface area
 *   stays small and replaceable.
 *
 * Adding a new variable:
 *   1. Add it to `.env.example` and `.env.local`.
 *   2. Add a derived getter below with a sensible default.
 *   3. Consumers import `env` from here — never `import.meta.env` directly.
 *
 * Before adding one, check whether it belongs in the admin settings instead.
 * Anything the shop owner might reasonably want to change — the brand name, a
 * phone number, a price — should live in StoreSetting and reach the storefront
 * through `useSettings()`, because a value baked in here can only be changed by
 * a developer running a rebuild and a redeploy.
 *
 * The branding, contact and delivery values below are exactly that: they are
 * now only the build-time FALLBACK behind `useSettings()`, used before the
 * settings fetch resolves and when no backend is configured. Read them through
 * the context, not from here, in anything that renders. The two API values are
 * the genuine exception — they are how the storefront reaches the settings
 * endpoint in the first place, so they can never come from it.
 */

/** @param {string} key @param {string} [fallback] */
const str = (key, fallback = '') => {
  const value = import.meta.env[key]
  return value === undefined || value === '' ? fallback : String(value)
}

/** @param {string} key @param {number} fallback */
const num = (key, fallback) => {
  const raw = import.meta.env[key]
  if (raw === undefined || raw === '') return fallback
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const env = Object.freeze({
  brandName: str('VITE_BRAND_NAME', 'Kiddo Corner'),
  brandTagline: str('VITE_BRAND_TAGLINE', 'Mother and baby needs'),

  supportPhone: str('VITE_SUPPORT_PHONE', '8801868617194'),
  whatsappNumber: str('VITE_WHATSAPP_NUMBER', '8801312227547'),
  messengerPageId: str('VITE_MESSENGER_PAGE_ID', 'YOURPAGEID'),
  contactEmail: str('VITE_CONTACT_EMAIL', 'kiddocornerbd@gmail.com'),
  // Homepage promo video; empty hides the section. Owner sets the real one
  // at /admin/settings.
  promoVideoUrl: str('VITE_PROMO_VIDEO_URL', ''),

  fbPixelId: str('VITE_FB_PIXEL_ID', ''),

  apiBaseUrl: str('VITE_API_BASE_URL', ''),
  apiTimeoutMs: num('VITE_API_TIMEOUT_MS', 15000),

  currencySymbol: str('VITE_CURRENCY_SYMBOL', '৳'),
  currencyCode: str('VITE_CURRENCY_CODE', 'BDT'),
  freeDeliveryThreshold: num('VITE_FREE_DELIVERY_THRESHOLD', 999),
  deliveryFee: num('VITE_DELIVERY_FEE', 80),
  deliveryFeeOutsideDhaka: num('VITE_DELIVERY_FEE_OUTSIDE_DHAKA', 150),

  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
})
